// DocumentPreview — what THIS company's document will actually look like.
//
// The value is that it is not a template gallery. go-pim renders the preview
// from live company state — the issuer block, the printing contacts, the
// default products that decompose the amount — so the question it answers is
// "did I fill this merchant in correctly", which no abstract template sample
// can answer. See go-pim/internal/adminapi/preview.go.
//
// Nothing here mints anything. Document numbers are gapless per
// (company_id, doc_type), so a "just generate one and look at it" preview would
// either burn RCPT-000001 or leave a permanent hole in a series a tax authority
// may read, and there is no delete that un-makes a gap. The rendered HTML
// carries PREVIEW in its number field for the same reason: a screenshot of this
// panel must never pass for an issued document.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal, Segmented } from "@/components/UI";
import { apiErrorMessage } from "@/utils/apiError";
import { parseMinorFromMajor } from "@/utils/money";
import { useAuth } from "@/store/auth";
import {
  previewDocument,
  PREVIEW_DOC_TYPES,
  PREVIEW_DOC_TYPE_LABELS,
  type PreviewDocType,
} from "@/api/preview";

const DOC_TYPE_OPTIONS = PREVIEW_DOC_TYPES.map((v) => ({
  value: v,
  label: PREVIEW_DOC_TYPE_LABELS[v],
}));

// The server's own default sample amount (₹85,000, chosen there to exercise a
// primary/secondary split). Seeding the field with the same figure means
// opening the panel and touching nothing shows exactly what an unparameterised
// preview shows — the control starts in agreement with the server rather than
// immediately contradicting it.
const DEFAULT_AMOUNT_MAJOR = "85000";
const DEFAULT_AMOUNT_MINOR = 8_500_000n;

// amount_minor lands in an int64 on the wire. Past that, go-pim's ParseInt
// fails and the handler SILENTLY falls back to its default sample — so an
// operator who pasted a huge figure would be shown ₹85,000 and have no way to
// tell. Refuse here instead of previewing an amount nobody asked for.
const MAX_INT64 = 9223372036854775807n;

// Typing "85000" is five keystrokes and would be five renders of a full
// document. The amount is committed on a short idle instead — the doc-type
// switch stays instant because it is a single deliberate click.
const AMOUNT_DEBOUNCE_MS = 350;

export function DocumentPreview({
  companyId,
  open,
  onClose,
}: {
  companyId: string;
  open: boolean;
  onClose: () => void;
}) {
  const status = useAuth((s) => s.status);
  const [docType, setDocType] = useState<PreviewDocType>("receipt");
  const [amountMajor, setAmountMajor] = useState(DEFAULT_AMOUNT_MAJOR);
  const [amountMinor, setAmountMinor] = useState(DEFAULT_AMOUNT_MINOR);

  // Money is int64 minor units everywhere in this system, so the rupees the
  // operator types are parsed as STRING DIGITS into paise — parseFloat("0.1")
  // is not 0.1 and a preview that silently previews the wrong figure is worse
  // than one that refuses. parseMinorFromMajor returns null for anything that
  // isn't a non-negative amount with at most two decimals.
  const parsed = parseMinorFromMajor(amountMajor);

  const exceedsInt64 = parsed !== null && parsed > MAX_INT64;

  const amountError =
    amountMajor.trim() === "" || parsed === null
      ? "Enter an amount in rupees, e.g. 85000 or 8500.50."
      : exceedsInt64
        ? "That's larger than the amount field can carry. Preview a smaller sample figure."
        : null;

  // Commit only VALID amounts. A half-typed "85000." must not blank the frame
  // that is already on screen — the operator is mid-keystroke, not asking for
  // an empty document.
  useEffect(() => {
    if (parsed === null || exceedsInt64) return;
    const t = setTimeout(() => setAmountMinor(parsed), AMOUNT_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [parsed, exceedsInt64]);

  const query = useQuery({
    // The amount enters the key as a DECIMAL STRING, not the bigint itself:
    // react-query hashes keys with JSON.stringify, which throws outright on a
    // bigint. Stringifying keeps the key exact at any magnitude; the bigint is
    // reconstructed for the call below, so nothing on the money path is ever a
    // number.
    queryKey: ["document-preview", companyId, docType, amountMinor.toString()],
    queryFn: () =>
      previewDocument(companyId, {
        doc_type: docType,
        amount_minor: amountMinor,
      }),
    enabled: open && status === "authenticated" && companyId !== "",
    // NEVER serve this from cache. The whole point is to check a company you
    // have just edited; a stale preview would show the issuer block as it was
    // before the save and read as "my change didn't take".
    staleTime: 0,
    // A 404 (company gone) or 400 (bad doc_type) is a settled answer, not a
    // blip. Retrying it three times only delays the message.
    retry: false,
    // Hold the previous document while the next one renders, so changing the
    // doc type doesn't flash an empty frame between two full pages.
    placeholderData: (prev) => prev,
  });

  const result = query.data;
  const notes = result?.notes ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="Document preview"
      foot={
        <>
          <div className="dim" style={{ fontSize: 11 }}>
            Nothing is issued and no document number is used up.
          </div>
          <div className="flex-1" />
          <button
            className="btn ghost"
            type="button"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            <I.Refresh size={11} /> {query.isFetching ? "Rendering…" : "Refresh"}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="stack-16">
        <div className="field">
          <span className="label">Document type</span>
          <Segmented
            value={docType}
            onChange={setDocType}
            options={DOC_TYPE_OPTIONS}
            label="Document type to preview"
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            You can preview a kind this merchant does not issue today — the
            preview says so rather than refusing.
          </div>
        </div>

        <div className="field" style={{ maxWidth: 220 }}>
          <label className="label" htmlFor="preview-amount">
            Sample amount
          </label>
          <div className="input-group">
            <span className="addon left">₹</span>
            <input
              id="preview-amount"
              className="input"
              type="text"
              inputMode="decimal"
              value={amountMajor}
              onChange={(e) => setAmountMajor(e.target.value)}
              aria-invalid={amountError !== null}
            />
          </div>
          <div
            className="dim"
            style={{
              fontSize: 11,
              marginTop: 4,
              color: amountError ? "var(--neg)" : undefined,
            }}
          >
            {/* Rupees here, paise on the wire. The preview endpoint has no
                currency option and defaults to INR, so labelling this in
                rupees is the honest label rather than a convenience. */}
            {amountError ??
              "In rupees. The merchant's default products decompose this into the lines the document will really carry."}
          </div>
        </div>

        {/* ── Notes ──
            These are the difference between a useful preview and a blank
            rectangle. A dark company, an empty issuer, no default products and
            no printing contact all render a document that looks broken but is
            correct — the note is the only thing that says which. Above the
            frame, not below it, because the operator's first reaction to an
            empty-looking document is to close the panel. */}
        {notes.length > 0 && (
          <div
            className="card"
            style={{ padding: 12, borderColor: "var(--warn)" }}
          >
            <div
              className="row gap-8"
              style={{ alignItems: "center", marginBottom: 6 }}
            >
              <I.AlertCircle size={13} />
              <strong style={{ fontSize: 12 }}>
                About this preview
              </strong>
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.55 }}>
              {notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </div>
        )}

        {query.isError ? (
          // Reported here rather than as a toast: the operator is looking at
          // this panel waiting for a document, and a message that slides away
          // after four seconds is the wrong place to explain why one never
          // arrived.
          <div className="empty">
            <div className="icon">
              <I.AlertCircle size={16} />
            </div>
            <h3>Couldn't render the preview</h3>
            <p>{apiErrorMessage(query.error, "The go-pim admin API rejected the preview request.")}</p>
          </div>
        ) : result ? (
          <div className="stack-8">
            <div className="dim" style={{ fontSize: 11 }}>
              {result.doc_type} · template{" "}
              <span className="mono">{result.template}</span>
              {query.isFetching ? " · rendering…" : ""}
            </div>
            {/* SANDBOXED FRAME, never dangerouslySetInnerHTML.
                go-pim escapes what it renders, so this is not primarily an XSS
                patch — it is that the payload is a WHOLE DOCUMENT with its own
                <style>. Injected into this panel's DOM, its rules apply to the
                app around it: a document that styles `body` or `table` would
                restyle the console the operator is standing in, and a print
                stylesheet would follow them to the printer. A frame is both the
                correct isolation and the honest representation — this thing is
                a standalone page, not a fragment of ours.

                sandbox="" is the empty allow-list: no scripts, no forms, no
                top-level navigation, and an opaque origin, so the frame cannot
                reach the session this panel is authenticated with. */}
            <iframe
              title={`Preview of a ${result.doc_type} for this merchant`}
              srcDoc={result.html}
              sandbox=""
              style={{
                width: "100%",
                height: 520,
                border: "1px solid var(--hair)",
                borderRadius: 4,
                // The document assumes paper. Without an explicit white ground
                // a transparent body would sit on the panel's dark theme and
                // render black-on-black — the operator would read that as a
                // broken template rather than a themed frame.
                background: "#fff",
              }}
            />
          </div>
        ) : (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              {query.isFetching ? "Rendering…" : "No preview yet."}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
